import { useEffect, useMemo, useState } from "react";
import shortid from "shortid";
import {
  Grid,
  TextField,
  Button,
  FormControl,
  Typography,
  Autocomplete,
  CurrencyInput,
  DataGrid,
} from "../../../../components";
import { notification } from "../../../../services";
import { value } from "../../../../helpers";
import {
  formatDate,
  formatPrecisaoFloat,
  formatPrice,
  // unformatPrice,
} from "../../../../utils";
import { parcelaData } from "../add-ordem-servico";
import { GridRowModes } from "@mui/x-data-grid";

const OrdemServicoListaPagamentos = ({
  id,
  configuracoes,
  ordemServico,
  setOrdemServico,
  listaOrdemServicoPagamentos,
  setListaOrdemServicoPagamentos,
  condicaoPagamentoList,
  gerarPagamentos,
  setGerarPagamentos,
  condicaoSelecionada,
  setCondicaoSelecionada,
  condicaoPagParcelaList,
  setCondicaoPagParcelaList,
  meioPagamentoList,
  meiosPagamentoCliente,
  ordemServicoEntity,
  meioPagamentoSelecionado,
  setMeioPagamentoSelecionado,
  listaOrdemServicoProdutos,
  setListaOrdemServicoProdutos,
  setAcrescimoPagamento,
  produto,
  setProduto,
  removerPagamentoCadastrado,
  isFechado,
  habilitarPagamentoPrazo,
  setReciboPagamentos,
}) => {
  const [rowModesModel, setRowModesModel] = useState({});
  const [inputError, setInputError] = useState(null);

  const listaMeioPagamento = useMemo(() => {
    if (!habilitarPagamentoPrazo) {
      return !configuracoes?.exibirPagamentoSemBaixa
        ? meioPagamentoList.filter(
            (meioPagamento) => meioPagamento.baixaAutomatica,
          )
        : meioPagamentoList;
    }
    return meioPagamentoList;
  }, [id, meioPagamentoList, habilitarPagamentoPrazo]);

  const colunas = [
    {
      field: "condicaoPagamentoId",
      headerName: "Condição de Pagamento",
      flex: 200,
      valueGetter: (params) =>
        value.autoComplete(condicaoPagamentoList, params.value).descricao,
    },
    {
      field: "meioPagamentoId",
      headerName: "Meio de Pagamento",
      flex: 300,
      sortable: false,
      valueGetter: (params) =>
        value.autoComplete(meioPagamentoList, params.value).descricao,
    },
    {
      field: "valorTitulo",
      headerName: "Valor do Título",
      flex: 230,
      sortable: false,
      valueGetter: (params) =>
        params.value > 0 ? formatPrice(params.value) : "R$ 0,00",
    },
    {
      field: "dataVencimento",
      headerName: "Vencimento",
      flex: 200,
      type: "date",
      editable: !id,
      sortable: false,
      valueGetter: (params) => {
        return formatDate.received(params.value);
      },
      valueParser: (newValue, params) => {
        const ordemServicoPagamentos = listaOrdemServicoPagamentos.map(
          (pagamento) => {
            if (pagamento.id === params.row.id) {
              return {
                ...params.row,
                dataVencimento: newValue
                  ? formatDate.received(newValue)
                  : pagamento.dataVencimento,
              };
            } else return pagamento;
          },
        );
        ordemServicoEntity.setChangePagamentos(ordemServicoPagamentos);
        setListaOrdemServicoPagamentos(ordemServicoPagamentos);
        return newValue;
      },
    },
    {
      field: "numeroAutorizacao",
      headerName: "Nº Autorização",
      flex: 200,
      editable: !ordemServico?.id ? true : false,
      sortable: false,
      valueGetter: (params) => params.value ?? "",
    },
  ];

  useEffect(() => {
    const valorTitulo =
      parseFloat(ordemServico?.totalOrdemServico ?? 0) -
      parseFloat(ordemServico?.totalPagamentos ?? 0);
    if (valorTitulo > 0) {
      setGerarPagamentos((prevState) => ({
        ...prevState,
        valorTitulo,
      }));
    }
  }, [ordemServico?.totalOrdemServico, ordemServico?.totalPagamentos]);

  const onChangeAutocomplete = (name, value) => {
    const ordemServicoPagamentoTemp = {
      ...gerarPagamentos,
      [name]: value ? value.id : null,
    };
    if (name === "condicaoPagamentoId") {
      const acrescimo = value?.pagamentoInfo?.acrescimo;
      setCondicaoSelecionada(value);
      setAcrescimoPagamento(acrescimo);
      recalcularProdutosAcrescimoPagamento(acrescimo);
      ordemServicoPagamentoTemp.quantidadeParcelas = value?.parcelas;
    }
    setGerarPagamentos(ordemServicoPagamentoTemp);
  };

  const handleChangeInput = (event) => {
    let valor = event.target.value;
    if (event.target.name === "numeroAutorizacao") {
      valor = valor ? regexNumeroAutorizacao(valor) : null;
    } else {
      valor = valor ? `${valor}`.toUpperCase() : null;
    }
    setGerarPagamentos({ ...gerarPagamentos, [event.target.name]: valor });
  };

  const validarGerarParcela = () => {
    if (!gerarPagamentos?.meioPagamentoId) {
      setInputError("meioPagamentoId");
      notification.erroValidacao("Meio de pagamento");
      return false;
    }
    if (!gerarPagamentos?.condicaoPagamentoId) {
      setInputError("condicaoPagamentoId");
      notification.erroValidacao("Condição de pagamento");
      return false;
    }
    if (!gerarPagamentos?.quantidadeParcelas) {
      setInputError("quantidadeParcelas");
      notification.erroValidacao("Quantidade de Parcelas");
      return false;
    }
    if (!gerarPagamentos?.valorTitulo || gerarPagamentos.valorTitulo <= 0) {
      setInputError("valorTitulo");
      notification.erroValidacao("Valor do título");
      return false;
    }
    if (
      meioPagamentoSelecionado?.cartaoCreditoDebito &&
      !gerarPagamentos?.numeroAutorizacao
    ) {
      setInputError("numeroAutorizacao");
      notification.erroValidacao("Número de Autorização");
      return true;
    }
    if (
      parseFloat(ordemServico?.totalPagamentos || 0) +
        parseFloat(gerarPagamentos.valorTitulo) >
      ordemServico.totalOrdemServico
    ) {
      notification.alertaGenericos(
        "Valor inserido ultrapassa total da Ordem de serviço",
      );
      return false;
    }
    return true;
  };

  const handleGerarParcelas = () => {
    const validarParcela = validarGerarParcela();
    if (!validarParcela) return;

    const parcelasGeradas = [];
    const valorParcela =
      gerarPagamentos.valorTitulo / gerarPagamentos.quantidadeParcelas;
    const valorFormatado = Math.floor(valorParcela * 100) / 100;
    const restoDivisao =
      gerarPagamentos.valorTitulo -
      valorFormatado * gerarPagamentos.quantidadeParcelas;
    for (
      let contador = 1;
      contador <= gerarPagamentos?.quantidadeParcelas;
      contador++
    ) {
      const valorTitulo =
        contador === gerarPagamentos.quantidadeParcelas
          ? valorFormatado + parseFloat(restoDivisao.toFixed(2))
          : valorFormatado;
      const id = shortid.generate();
      parcelasGeradas.push({
        id,
        numeroTitulo:
          id && listaOrdemServicoPagamentos.length
            ? listaOrdemServicoPagamentos.length + contador
            : contador,
        condicaoPagamentoId: gerarPagamentos?.condicaoPagamentoId,
        meioPagamentoId: gerarPagamentos?.meioPagamentoId,
        dataVencimento: parcelaData(contador, condicaoSelecionada?.periodo),
        numeroAutorizacao: gerarPagamentos?.numeroAutorizacao,
        meioPagamentoSelecionado,
        valorTitulo,
      });
      if (gerarPagamentos?.quantidadeParcelas === 1) {
        setReciboPagamentos((prev) => {
          const temp = {
            id,
            meioPagamento: meioPagamentoSelecionado.descricao,
            condicaoPagamento: condicaoSelecionada.descricao,
            numeroAutorizacao: gerarPagamentos?.numeroAutorizacao,
            valorPago: valorFormatado,
            dataPagamento: new Date(),
          };
          return [...prev, temp];
        });
      }
    }
    ordemServicoEntity.setChangePagamentos([
      ...listaOrdemServicoPagamentos,
      ...parcelasGeradas,
    ]);
    setListaOrdemServicoPagamentos((prevState) => [
      ...prevState,
      ...parcelasGeradas,
    ]);
    setOrdemServico(ordemServicoEntity);
    setGerarPagamentos({
      valorTitulo:
        ordemServicoEntity?.totalOrdemServico -
        parseFloat(ordemServicoEntity?.totalPagamentos ?? 0),
    });
    notification.sucessoGenericos("Parcelas geradas com sucesso");
  };

  const onSubmitParcelas = (event) => {
    event.preventDefault();
    handleGerarParcelas();
  };

  const onClickDeletePagamentos = async (idParcela) => {
    const parcelaADeletar = listaOrdemServicoPagamentos.find(
      ({ id }) => id == idParcela,
    );
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir essa parcela?`,
    );
    if (retornoAlerta.isConfirmed) {
      if (id && typeof idParcela === "number") {
        removerPagamentoCadastrado([idParcela]);
      } else {
        const data = listaOrdemServicoPagamentos.filter(
          (item) => item.id !== idParcela,
        );
        setGerarPagamentos({
          ...gerarPagamentos,
          valorTitulo:
            gerarPagamentos?.valorTitulo + parcelaADeletar?.valorTitulo,
        });
        setReciboPagamentos((prev) =>
          prev.filter((item) => item.id !== idParcela),
        );
        setListaOrdemServicoPagamentos(data);
        ordemServicoEntity.setChangePagamentos(data);
        setOrdemServico(ordemServicoEntity);
      }
    }
  };

  const onClickLimparParcelas = () => {
    setGerarPagamentos({
      ...gerarPagamentos,
      valorTitulo: ordemServico.totalOrdemServico,
    });
    setListaOrdemServicoPagamentos([]);
    setReciboPagamentos([]);
    ordemServicoEntity.setChangePagamentos(null);
    setOrdemServico(ordemServicoEntity);
  };

  const onChangeMeioPagamento = (id, value) => {
    const condicoesPagamento = value?.condicoesPagamento ?? [];
    setMeioPagamentoSelecionado(value);
    let acrescimo = null;
    let gerarPagamentosTemp = {
      [id]: value?.id ?? null,
      condicaoPagamentoId: null,
      quantidadeParcelas: null,
    };
    const primeiraCondicao = condicoesPagamento[0];
    const condicaoPagamentoUnica = condicoesPagamento.length === 1;
    if (condicaoPagamentoUnica) {
      acrescimo = primeiraCondicao?.pagamentoInfo?.acrescimo ?? null;
      gerarPagamentosTemp = {
        [id]: value?.id ?? null,
        condicaoPagamentoId: primeiraCondicao?.id ?? null,
        quantidadeParcelas: primeiraCondicao?.parcelas ?? null,
      };
    }
    setGerarPagamentos((prev) => ({
      ...prev,
      ...gerarPagamentosTemp,
    }));
    setCondicaoSelecionada(condicaoPagamentoUnica ? primeiraCondicao : null);
    setAcrescimoPagamento(acrescimo);
    recalcularProdutosAcrescimoPagamento(acrescimo);
    setCondicaoPagParcelaList(condicoesPagamento);
  };

  function recalcularProdutosAcrescimoPagamento(acrescimo) {
    const calculoValorUnitario = (acrescimo, produto) =>
      acrescimo > 0
        ? formatPrecisaoFloat(
            produto?.valorUnitario + produto?.valorUnitario * (acrescimo / 100),
          )
        : produto?.valorUnitario;
    const calculoDesconto = (valor, produto) =>
      produto?.descontoPorcentagem
        ? (valor *
            produto?.quantidade *
            parseFloat(produto?.descontoPorcentagem)) /
          100
        : produto?.desconto;
    const valorUnitario = calculoValorUnitario(acrescimo, produto);
    if (produto) {
      setProduto((prevState) => ({
        ...prevState,
        valorUnitario,
        totalItem:
          valorUnitario * prevState?.quantidade -
          (calculoDesconto(valorUnitario, prevState) || 0),
        desconto: calculoDesconto(valorUnitario, prevState),
      }));
    }
    if (listaOrdemServicoProdutos?.length) {
      const listaItens = listaOrdemServicoProdutos.map((item) => {
        const valorUnitario = calculoValorUnitario(acrescimo, item);
        return {
          ...item,
          valorUnitario,
          desconto: calculoDesconto(valorUnitario, item),
          totalItem:
            valorUnitario * item.quantidade -
            (calculoDesconto(valorUnitario, item) || 0),
        };
      });
      setListaOrdemServicoProdutos(listaItens);
      ordemServicoEntity.setChangeItens(listaItens);
      setOrdemServico({ ...ordemServicoEntity });
    }
  }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const regexNumeroAutorizacao = (numeroAutorizacao) =>
    `${numeroAutorizacao}`
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 25)
      .toUpperCase();

  const onCellKeyDown = (params, event) => {
    if (event.key !== "Enter" && event.key !== "Tab") return;
    let updatedValue = event.target.value ? event.target.value : null;
    if (params.field === "dataVencimento") {
      updatedValue = formatDate.toSend(formatDate.format(updatedValue));
    }
    const pagamentos = listaOrdemServicoPagamentos.map((pagamento) => {
      const isMatchingPagamento =
        params.row.meioPagamentoId === pagamento.meioPagamentoId;
      if (
        isMatchingPagamento &&
        (params.field === "numeroAutorizacao" || params.row.id === pagamento.id)
      ) {
        let fieldValue =
          params.field === "numeroAutorizacao"
            ? updatedValue
              ? regexNumeroAutorizacao(event.target.value)
              : null
            : updatedValue;
        return { ...pagamento, [params.field]: fieldValue };
      }
      return pagamento;
    });
    setListaOrdemServicoPagamentos(pagamentos);
    ordemServicoEntity.setChangePagamentos(pagamentos);
    setOrdemServico(ordemServicoEntity);
  };

  return (
    <>
      <Grid container>
        <Grid container spacing={2} xs={4} className="d-block">
          <Grid item xs={12}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="meioPagamentoId"
                name="meioPagamentoId"
                disabled={isFechado}
                options={
                  meiosPagamentoCliente?.length
                    ? meiosPagamentoCliente
                    : listaMeioPagamento
                }
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Meio de Pagamento"
                    variant="outlined"
                    error={inputError == "meioPagamentoId" ? true : false}
                    required
                  />
                )}
                onChange={(_, newValue) =>
                  onChangeMeioPagamento("meioPagamentoId", newValue)
                }
                value={value.autoComplete(
                  meiosPagamentoCliente?.length
                    ? meiosPagamentoCliente
                    : listaMeioPagamento,
                  gerarPagamentos?.meioPagamentoId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                required
                id="condicaoPagamentoId"
                name="condicaoDePagamento"
                disabled={isFechado}
                options={condicaoPagParcelaList}
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                getOptionSelected={(option, value) =>
                  option?.descricao === value?.descricao
                }
                noOptionsText="Sem opções"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Condição de Pagamento"
                    variant="outlined"
                    error={inputError == "condicaoPagamentoId" ? true : false}
                    required
                  />
                )}
                onChange={(e, newValue) => {
                  e.persist();
                  onChangeAutocomplete("condicaoPagamentoId", newValue);
                }}
                value={value.autoComplete(
                  condicaoPagParcelaList,
                  gerarPagamentos?.condicaoPagamentoId,
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <CurrencyInput
              id="valorTitulo"
              name="valorTitulo"
              label="Valor do Título (R$)"
              inputVariant="outlined"
              required
              disabled={isFechado}
              error={inputError == "valorTitulo" ? true : false}
              value={gerarPagamentos?.valorTitulo ?? ""}
              onChange={(event) => {
                setInputError(null);
                handleChangeInput(event);
              }}
              fullWidth
            />
          </Grid>
          {meioPagamentoSelecionado?.cartaoCreditoDebito && (
            <Grid item xs={12}>
              <TextField
                id="numeroAutorizacao"
                name="numeroAutorizacao"
                label="Número Autorização"
                inputVariant="outlined"
                margin="none"
                required
                disabled={isFechado}
                error={inputError == "numeroAutorizacao"}
                value={gerarPagamentos?.numeroAutorizacao ?? ""}
                inputProps={{ maxLength: 25 }}
                onChange={(event) => {
                  setInputError(null);
                  handleChangeInput(event);
                }}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12} fullWidth>
            <Button
              fullWidth
              className="mt-3 mb-3"
              size="large"
              variant="contained"
              disabled={isFechado}
              color="primary"
              onClick={onSubmitParcelas}
            >
              <i
                className="ph-fill ph-plus-circle"
                style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
              ></i>
              Gerar Parcelas
            </Button>
          </Grid>
        </Grid>
        <Grid
          container
          xs={1}
          fullWidth
          className="d-flex justify-content-center"
        >
          <hr className="vr" />
        </Grid>
        <Grid container xs={7} className="d-block mt-3">
          <Grid
            container
            xs={12}
            fullWidth
            className="d-flex justify-content-between align-items-center"
          >
            <Typography variant="overline">
              <h4 style={{ fontWeight: 800, margin: 0 }}>PARCELAS</h4>
            </Typography>
            {!id && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={onClickLimparParcelas}
              >
                <i
                  className="ph-fill ph-trash"
                  style={{ color: "#2196F3", fontSize: 18, marginRight: 5 }}
                ></i>
                Limpar Parcelas
              </Button>
            )}
          </Grid>
          <Grid container xs={12} fullWidth className="mt-4">
            <DataGrid
              rows={listaOrdemServicoPagamentos}
              columns={colunas}
              rowCount={listaOrdemServicoPagamentos.length}
              deleteShow={!ordemServico?.id}
              sortModel={[{ field: "condicaoPagamentoId", sort: "desc" }]}
              onClickDelete={onClickDeletePagamentos}
              rowModesModel={rowModesModel}
              onCellKeyDown={onCellKeyDown}
              onRowModesModelChange={handleRowModesModelChange}
              onCellEditStop={(params) => {
                if (
                  params.reason === "cellFocusOut" &&
                  params.field !== "dataVencimento"
                ) {
                  const newValue = params.value ?? null;
                  if (newValue !== undefined) {
                    setListaOrdemServicoPagamentos((prev) =>
                      prev.map((r) =>
                        r.id === params.id
                          ? { ...r, [params.field]: newValue }
                          : r,
                      ),
                    );
                  }
                  notification.alertaGenericos(
                    "Pressione Enter para salvar as alterações",
                  );
                }

                setRowModesModel((prev) => ({
                  ...prev,
                  [params.id]: {
                    mode: GridRowModes.View,
                  },
                }));
              }}
              disableAcoes={isFechado}
            />
          </Grid>
          <Grid
            container
            xs={12}
            fullWidth
            className="d-flex align-items-center justify-content-end mt-3"
          >
            <Grid
              item
              xs={5}
              className="d-flex align-items-center justify-content-end"
            >
              <span className="me-2">Saldo a pagar:</span>
              <span className="badge bg-secondary bg-gradient bg-gradient text-wrap p-2">
                {formatPrice(
                  (ordemServico?.totalOrdemServico || 0) -
                    (ordemServico?.totalPagamentos || 0),
                ) || "R$ 0,00"}
              </span>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default OrdemServicoListaPagamentos;
